import { useEffect, useState } from 'react'
import { requestWorkspaceSwitch } from '../workspaceSwitchService'

export interface WorkspaceAccessLostPromptRequest {
  workspaceName: string
  reason: 'removed' | 'deleted'
  toWorkspaceId: string | null
  landing?: string | null
}

let promptHandler: ((request: WorkspaceAccessLostPromptRequest) => void) | null = null
let pendingRequest: WorkspaceAccessLostPromptRequest | null = null
let activeRequest: WorkspaceAccessLostPromptRequest | null = null
let fallbackCancel: (() => void) | null = null
const stateListeners = new Set<(active: boolean) => void>()

const notifyListeners = () => {
  const active = Boolean(activeRequest || pendingRequest)
  stateListeners.forEach((listener) => {
    try {
      listener(active)
    } catch {
      // ignore
    }
  })
}

export const subscribeAccessLostPromptActive = (listener: (active: boolean) => void): (() => void) => {
  stateListeners.add(listener)
  listener(Boolean(activeRequest || pendingRequest))
  return () => {
    stateListeners.delete(listener)
  }
}

export const useIsAccessLostPromptActive = (): boolean => {
  const [active, setActive] = useState(() => Boolean(activeRequest || pendingRequest))
  useEffect(() => {
    return subscribeAccessLostPromptActive(setActive)
  }, [])
  return active
}

export const isAccessLostPromptActive = (): boolean => {
  return Boolean(activeRequest || pendingRequest)
}

export const getAccessLostRequest = (): WorkspaceAccessLostPromptRequest | null => {
  return activeRequest || pendingRequest
}

export const registerWorkspaceAccessLostPrompt = (
  handler: (request: WorkspaceAccessLostPromptRequest) => void,
): (() => void) => {
  promptHandler = (req) => {
    activeRequest = req
    notifyListeners()
    handler(req)
  }
  if (fallbackCancel) {
    fallbackCancel()
    fallbackCancel = null
  }
  if (pendingRequest) {
    const req = pendingRequest
    pendingRequest = null
    promptHandler(req)
  }
  return () => {
    promptHandler = null
    activeRequest = null
    notifyListeners()
  }
}

export const promptWorkspaceAccessLost = (request: WorkspaceAccessLostPromptRequest) => {
  if (promptHandler) {
    promptHandler(request)
  } else {
    pendingRequest = request
    notifyListeners()
    // No-UI fallback: if no sheet is mounted, commit direct switch on microtask turn
    if (fallbackCancel) fallbackCancel()
    let cancelled = false
    fallbackCancel = () => {
      cancelled = true
    }
    queueMicrotask(() => {
      if (!cancelled && pendingRequest === request && !promptHandler) {
        pendingRequest = null
        fallbackCancel = null
        notifyListeners()
        void requestWorkspaceSwitch({
          source: 'MEMBERSHIP_ENDED',
          toWorkspaceId: request.toWorkspaceId,
          landing: request.landing,
        })
      }
    })
  }
}

export const clearAccessLostPromptState = () => {
  if (fallbackCancel) {
    fallbackCancel()
    fallbackCancel = null
  }
  activeRequest = null
  pendingRequest = null
  notifyListeners()
}
